<script setup>
import { computed, toRefs, ref } from 'vue'
import { Country } from 'country-state-city'
import StateField from './StateField.vue'
import BaseListbox from '@/components/Listbox/BaseListbox.vue'
import { useUserStore } from '@/stores/UserStore'
import { getFlagEmoji } from '@/helpers/general'
import { useGoogleAddressAutocomplete } from '@/hooks/useGoogleAddressAutocomplete'

const userStore = useUserStore()

const props = defineProps({
  editMode: { type: Boolean, default: true },
  location: { type: Object, default: {} },
  errors: { type: Array, default: [] },
  hideFields: { type: Array, default: [] },
  hideLabel: { type: Boolean, default: false },
  supportedCountries: { type: Array, default: [] },
  stateNextToCountry: { type: Boolean, default: false },
  /** 'default' = current layout; 'grid' = two-column with required asterisks and country flag */
  layout: { type: String, default: 'default' },
  labelClass: { type: String, default: 'controller-label' },
  inputClass: { type: String, default: 'input-controller' },
  selectClass: { type: String, default: 'select-controller' },
  placeholderNon: { type: Boolean, default: false },
})

const addressInputRef = ref(null)

const { editMode, location, errors } = toRefs(props)

const isGridLayout = computed(() => props.layout === 'grid')

const countryOptions = computed(() => {
  const list = props.supportedCountries.length > 0 ? props.supportedCountries : Country.getAllCountries()

  return list.map((country) => ({
    value: country.isoCode,
    label: country.name,
    flag: getFlagEmoji(country.isoCode),
  }))
})

const requiredMark = (field) => (isGridLayout.value ? ' *' : '');

const applyPlace = (parsed) => {
  if (parsed.address_1)   location.value.address_1   = parsed.address_1
  if (parsed.city)        location.value.city        = parsed.city
  if (parsed.state)       location.value.state       = parsed.state
  if (parsed.postal_code) location.value.postal_code = parsed.postal_code
  if (parsed.country)     location.value.country     = parsed.country
}

useGoogleAddressAutocomplete({
  inputRef: addressInputRef,
  onSelect: applyPlace,
  country: () => location.value.country,
})
</script>
<template>
  <section>
    <!-- Grid layout: two-column, required asterisks, country with flag -->
    <div
      v-if="isGridLayout"
      class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 text-xs font-normal text-gray-900"
    >
      <label v-if="!hideFields.includes('address_1')" class="block">
        <span :class="[labelClass]">Address Line 1{{ requiredMark('address_1') }}</span>
        <template v-if="editMode">
          <input
            ref="addressInputRef"
            type="text"
            class="font-normal py-2 w-full"
            :placeholder="placeholderNon ? '' : 'Address Line 1'"
            :class="[inputClass, { error: errors.includes('address_1') || errors.includes('addressLine1') }]"
            v-model="location.address_1"
          />
          <small v-if="errors.includes('address_1') || errors.includes('addressLine1')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
        <p v-else class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_1 && !location.addressLine1 }">{{ location.address_1 || location.addressLine1 || '-' }}</p>
      </label>
      <label v-if="!hideFields.includes('address_2')" class="block">
        <span :class="[labelClass]">Address Line 2</span>
        <template v-if="editMode">
          <input
            type="text"
            class="font-normal py-2 w-full"
            :class="[inputClass]"
            :placeholder="placeholderNon ? '' : 'Address Line 2'"
            v-model="location.address_2"
          />
        </template>
        <p v-else class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_2 }">{{ location.address_2 || '-' }}</p>
      </label>

      <div v-if="!hideFields.includes('country')" class="block">
        <template v-if="editMode">
          <BaseListbox
            v-model="location.country"
            :options="countryOptions"
            :label="hideLabel ? '' : 'Country' + requiredMark('country')"
            :placeholder="placeholderNon ? '' : 'Select Country'"
            :error="errors.includes('country')"
            button-class="w-full"
            :is-capitalize="false"
            :label-class="hideLabel ? '' : labelClass"
            option-value-key="value"
            option-label-key="label"
            :use-portal="true"
            :placeholder-non="placeholderNon"
            :input-class="inputClass"
            :select-class="selectClass"
          >
            <template #button-content>
              <span class="flex-1 text-left" v-if="location.country">{{ location.country }}</span>
              <span class="flex-1 text-left text-gray-500" v-else>Select Country</span>
            </template>
          </BaseListbox>
          <small v-if="errors.includes('country')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
        <div v-else class="flex flex-col items-start">
          <span :class="[labelClass]">Country</span>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.country }">
            {{ countryOptions.find((o) => o.value === location.country)?.label || '-' }}
          </p>
        </div>
      </div>
      <label v-if="!hideFields.includes('postal_code')" class="block">
        <span :class="[labelClass]">{{ location.country === 'US' ? 'ZIP Code' : 'Postal Code' }}{{ requiredMark('postal_code') }}</span>
        <template v-if="editMode">
          <input
            type="text"
            class="font-normal py-2 w-full"
            :placeholder="placeholderNon ? '' : (location.country === 'US' ? 'ZIP Code' : 'Postal Code')"
            :class="[inputClass, { error: errors.includes('postal_code') || errors.includes('postalCode') }]"
            v-model="location.postal_code"
          />
          <small v-if="errors.includes('postal_code') || errors.includes('postalCode')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
        <p v-else class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.postal_code && !location.postalCode }">{{ location.postal_code || location.postalCode || '-' }}</p>
      </label>

      <label v-if="!hideFields.includes('city')" class="block">
        <span class="controller-label">City{{ requiredMark('city') }}</span>
        <template v-if="editMode">
          <input
            type="text"
            class="font-normal py-2 w-full"
            :placeholder="placeholderNon ? '' : 'City'"
            :class="[inputClass, { error: errors.includes('city') }]"
            v-model="location.city"
          />
          <small v-if="errors.includes('city')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
        <p v-else class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.city }">{{ location.city || '-' }}</p>
      </label>
      <div v-if="!hideFields.includes('state')" class="block">
        <StateField
          :location="location"
          :errors="errors"
          :edit-mode="editMode"
          :hide-label="hideLabel"
          :label-suffix="requiredMark('state')"
          :placeholder-non="placeholderNon"
          :input-class="inputClass"
          :label-class="labelClass"
        />
      </div>
    </div>

    <!-- Default layout: unchanged for all existing usages -->
    <div
      v-else
      class="flex flex-col gap-y-4 text-xs font-normal text-gray-900"
    >
      <div :class="{ 'flex flex-col md:flex-row gap-y-3 md:gap-y-0 md:gap-x-4': stateNextToCountry }">
        <div class="block" :class="{ 'flex-1': stateNextToCountry }">
          <template v-if="editMode">
            <BaseListbox
              v-model="location.country"
              :options="countryOptions"
              :label="hideLabel ? '' : 'Country'"
              :placeholder="placeholderNon ? '' : 'Select Country'"
              :error="errors.includes('country')"
              button-class="w-full"
              :is-capitalize="false"
              :label-class="hideLabel ? '' : labelClass"
              :input-class="inputClass"
              :select-class="selectClass"
            />
            <small v-if="errors.includes('country')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <div v-else class="flex flex-col items-start ">
            <span class="controller-label">Country</span>
            <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.country }">{{ countryOptions.find(option => option.value === location.country)?.label || '-' }}</p>
          </div>
        </div>

        <StateField
          v-if="stateNextToCountry && !hideFields.includes('state')"
          class="flex-1"
          :location="location"
          :errors="errors"
          :edit-mode="editMode"
          :hide-label="hideLabel"
          :label-class="labelClass"
          :placeholder-non="placeholderNon"
          :input-class="inputClass"
        />
      </div>

      <label class="block" v-if="!hideFields.includes('address_1')">
        <span :class="[labelClass]">Address Line 1</span>
        <template v-if="editMode">
          <input
            ref="addressInputRef"
            type="text"
            class="font-normal py-2"
            :placeholder="placeholderNon ? '' : 'Address Line 1'"
            :class="[inputClass, { error: errors.includes('address_1') }]"
            v-model="location.address_1"
          />
          <small v-if="errors.includes('address_1')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
       
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_1 }" v-else>{{ location.address_1 || '-' }}</p>
      </label>
     
      <label class="block" v-if="!hideFields.includes('address_2')">
        <span :class="[labelClass]">Address Line 2 <span class="text-gray-500">(Optional)</span></span>
        <template v-if="editMode">
          <input
            type="text"
            class="font-normal py-2"
            :class="[inputClass]"
            :placeholder="placeholderNon ? '' : 'Address Line 2 (Optional)'"
            v-model="location.address_2"
          />
        </template>
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_2 }" v-else>{{ location.address_2 || '-' }}</p>
      </label>
     
      <div
        class="flex gap-x-4"
        v-if="!hideFields.includes('city') || !hideFields.includes('postal_code') || (!stateNextToCountry && !hideFields.includes('state'))"
      >
        <label class="block" v-if="!hideFields.includes('city')">
          <span :class="[labelClass]">City</span>
          <template v-if="editMode">
            <input
              type="text"
              class="font-normal py-2"
              :placeholder="placeholderNon ? '' : 'City'"
              :class="[inputClass, { error: errors.includes('city') }]"
              v-model="location.city"
            />
            <small v-if="errors.includes('city')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.city }" v-else>{{ location.city || '-' }}</p>
        </label>

        <StateField
          v-if="!stateNextToCountry && !hideFields.includes('state')"
          :location="location"
          :errors="errors"
          :edit-mode="editMode"
          :hide-label="hideLabel"
          :label-class="labelClass"
          :placeholder-non="placeholderNon"
          :input-class="inputClass"
        />

        <label class="block" v-if="!hideFields.includes('postal_code')">
          <span :class="[labelClass]">{{ location.country === 'US' ? 'ZIP Code' : 'Postal Code' }}</span>
          <template v-if="editMode">
            <input
              type="text"
              class="font-normal py-2"
              :placeholder="placeholderNon ? '' : (location.country === 'US' ? 'ZIP Code' : 'Postal Code')"
              :class="[inputClass, { error: errors.includes('postal_code') }]"
              v-model="location.postal_code"
            />
            <small v-if="errors.includes('postal_code')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.postal_code }" v-else>{{ location.postal_code || '-' }}</p>
        </label>
      </div>
    </div>
  </section>
</template>

<!--
  Google Places renders its suggestion dropdown (`.pac-container`) attached to
  <body>, so it must out-stack modal overlays (e.g. AddressDialog). Unscoped on
  purpose — the element lives outside this component's DOM.
-->
<style>
.pac-container {
  z-index: 100000 !important;
}
</style>